import { LocationType } from '@shared/enums/location-type.enum';
import { Injectable } from '@angular/core';
import { ApiBaseService } from '@core/services/api-base.service';
import { Mapper } from '@core/services/mapper/base-mapper.mapper';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Lead } from '@shared-features/models/lead';
import { ApiConstant } from '@shared/constants/api.constant';
import { DynamicScriptService } from '@shared/services/dynamic-script.service';
import { LazyLibId } from '@shared/enums/lazy-lib-id.enum';
import { environment } from '@env/environment';
import { filterNullEntity } from '@shared/utils/filter-null-entity.util';
import { WCCRegion } from '@features/shipment-creation-template/models/wcc-region.model';
import { WCCReceiver } from '@features/shipment-creation-template/models/wcc-receiver.model';
import { LocationModel } from '@features/live-tracking/models/location.model';

@Injectable({
  providedIn: 'root',
})
export class LocationsApiService {
  isGoogleMapLoaded = false;

  constructor(
    private baseAPI: ApiBaseService,
    private mapper: Mapper,
    private dynamicScriptService: DynamicScriptService
  ) {
    this.loadGoogleMapsApi();
  }

  loadGoogleMapsApi(): Promise<void> {
    const mapsUrl = `https://maps.googleapis.com/maps/api/js?key=${environment.google_map_key}&libraries=places`;
    const clustererUrl = 'https://unpkg.com/@googlemaps/markerclustererplus/dist/index.min.js';

    return Promise.all([
      this.dynamicScriptService.loadDynamicScript(LazyLibId.googleMaps, mapsUrl),
      this.dynamicScriptService.loadDynamicScript(LazyLibId.googleMapsClusterer, clustererUrl),
    ]).then(() => {
      this.isGoogleMapLoaded = true;
      return Promise.resolve();
    });
  }

  addLocation(
    location: LocationModel,
    id: number,
    usersIds: number[] = [],
    defaultUserId?: number
  ): Observable<LocationModel> {
    const body = filterNullEntity({
      type: location.type,
      id: id,
      title: location.title,
      gates: location.gates,
      address: location.address,
      cityId: location.cityId,
      latitude: location.latitude,
      longitude: location.longitude,
      shippingType: location.shippingType,
      usersIds: usersIds,
      defaultUserId: defaultUserId,
      wccRegionId: location?.wccRegion?.id,
      wccReceiverId: location?.wccReceiver?.id,
      reference: location?.reference,
    });

    return this.baseAPI.post(ApiConstant.ADD_LOCATION, body).pipe(
      map((res) => {
        return this.mapper.fromJson(LocationModel, res.data);
      })
    );
  }

  editLocation(location: Partial<LocationModel>): Observable<LocationModel> {
    return this.baseAPI.put(ApiConstant.EDIT_LOCATION, location).pipe(
      map((res) => {
        return this.mapper.fromJson(LocationModel, res.data);
      })
    );
  }

  getLocations(locationType: LocationType, id: number): Observable<LocationModel[]> {
    return this.baseAPI.get(ApiConstant.LOCATION_GET_ALL + `?type=${locationType}&id=${id}`).pipe(
      map((res) => {
        return this.mapper.fromJson(LocationModel, res.data);
      })
    );
  }

  getWCCRegions(id: number): Observable<WCCRegion[]> {
    return this.baseAPI.get(ApiConstant.API_GET_WCC_REGIONS + `/${id}`).pipe(
      map((res) => {
        return this.mapper.fromJson(WCCRegion, res.data);
      })
    );
  }

  getWCCReceivers(regionId: number, customerId: number): Observable<WCCReceiver[]> {
    return this.baseAPI.get(ApiConstant.API_GET_WCC_RECEIVERS + `/${regionId}/${customerId}`).pipe(
      map((res) => {
        return this.mapper.fromJson(WCCReceiver, res.data);
      })
    );
  }

  getLocation(locationId: number, ownerId?: number): Observable<LocationModel> {
    return this.baseAPI
      .get(`${ApiConstant.GET_LOCATION_DETAILS}/${locationId}`)
      .pipe(map((res) => this.mapper.fromJson(LocationModel, res.data)));
  }

  getLeadDriversLocations(): Observable<Lead[]> {
    return this.baseAPI
      .get(ApiConstant.GET_LEAD_DRIVERS_LOCATIONS, { params: { limit: 1000, offset: 0 } })
      .pipe(map((res) => this.mapper.fromJson(Lead, res.data)));
  }
}
